const Konashi = require('../konashijs/konashi.js');
const log = require('../../util/log');

class _PioPin {
    constructor (pin) {
        this.number = pin;
        this.pioMode = Konashi.consts.INPUT;
        this.pullup = Konashi.consts.NO_PULLS;
        this.input = Konashi.consts.LOW;
        this.output = Konashi.consts.LOW;
        this.pwmMode = Konashi.consts.KONASHI_PWM_DISABLE;
        this.pwmPeriod = 0;
        this.pwmDuty = 0;
        this.pwmRatio = 0;
    }
}

class _AnalogPin {
    constructor (pin) {
        this.number = pin;
        this.input = -1;
    }
}

const BLE_DELAY = {
    NONE: 0,
    NORMAL: 40,
    LONG: 100,
    READ: 200,
    DEBUG: 1000
};

/**
 * TODO: test this function
 */
class Queue {
    constructor () {
        let queue = Promise.resolve(true);

        const add = function (job, delay) {
            const p = function () {
                return new Promise(resolve => {
                    setTimeout(() => {
                        job();
                        resolve();
                    }, delay);
                });
            };
            queue = queue.then(p);
        };

        this.add = add;
    }
}

class KokoroPro {
    constructor (onReceived) {
        this._queue = new Queue();
        this._konashi = null;
        this._name = null;
        this._onReceived = onReceived;
        this._isConnectionFailed = false;

        this._pioPins = [
            new _PioPin(0),
            new _PioPin(1),
            new _PioPin(2),
            new _PioPin(3),
            new _PioPin(4),
            new _PioPin(5),
            new _PioPin(6),
            new _PioPin(7)
        ];

        this._analogPins = [
            new _AnalogPin(0),
            new _AnalogPin(1),
            new _AnalogPin(2)
        ];
    }

    getConsts () {
        return {
            PIO0: Konashi.consts.PIO0,
            PIO1: Konashi.consts.PIO1,
            PIO2: Konashi.consts.PIO2,
            PIO3: Konashi.consts.PIO3,
            PIO4: Konashi.consts.PIO4,
            PIO5: Konashi.consts.PIO5,
            PIO6: Konashi.consts.PIO6,
            PIO7: Konashi.consts.PIO7,
            AIO0: Konashi.consts.AIO0,
            AIO1: Konashi.consts.AIO1,
            AIO2: Konashi.consts.AIO2,
            HIGH: Konashi.consts.HIGH,
            LOW: Konashi.consts.LOW
        };
    }

    connect (prefix) {
        if (this._konashi) this._konashi.disconnect();

        return Konashi.funcs
            .find(true, prefix, null, () => {
                this._isConnectionFailed = true;
            })
            .then(k => {
                this._konashi = k;
                this._name = k.name();
                this._connected = true;
            })
            .then(() => {
                this._startDigitalRead();
            });
    }

    /**
     * TODO: test this function
     */
    reset () {
        this._queue.reset();
        this._konashi.reset();
    }

    isConnected () {
        let connected = false;
        if (this._konashi) {
            connected = this._konashi.isConnected();
        }
        return connected;
    }

    isConnectionFailed () {
        return this._isConnectionFailed;
    }


    /**
     * @param {Konashi.consts.PIOX} pid PIO pin number (0-7)
     * @param {Konashi.consts.INPUT|OUTPUT} mode pin mode
     */
    _setMode (pid, mode) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;
        if (mode == this._pioPins[pid].pinMode) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.pinMode(pid, mode);
            that._pioPins[pid].pinMode = mode;
        }, BLE_DELAY.LONG);
    }

    /**
     * Confirm if the pin mode is Konashi.consts.OUTPUT before call this function.
     *
     * @param {Konashi.consts.PIOX} pid PIO pin number (0-7)
     * @param {Konashi.consts.HIGI|LOW} value output value
     */
    setOutput (pid, value) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;

        let isModeChanged = false;

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_DISABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_DISABLE);
            isModeChanged = true;
        }

        if (this._pioPins[pid].pioMode == Konashi.consts.INPUT) {
            this._setMode(pid, Konashi.consts.OUTPUT);
            isModeChanged = true;
        }

        if (!isModeChanged && this._pioPins[pid].output == value) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.digitalWrite(pid, value);
            that._pioPins[pid].output = value;
        }, BLE_DELAY.NORMAL);
    }

    /**
     * @param {*} pid PIO pin number (0-7)
     * @param {*} mode Konashi.consts.KONASHI_PWM_DISABLE|KONASHI_PWM_ENABLE|KONASHI_PWM_ENABLE_LED_MODE
     */
    _setPwmMode (pid, mode) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;
        if (mode == this._pioPins[pid].pwmMode) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmMode(pid, mode);
            that._pioPins[pid].pwmMode = mode;
        }, BLE_DELAY.LONG);
    }

    /**
     * @param {*} pid PIO pin number (0-7)
     * @param {Int} ratio 0 - 100
     */
    pwmLedDrive (pid, ratio) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;

        let isModeChanged = false;

        if (this._pioPins[pid].pioMode == Konashi.consts.INPUT) {
            this._setMode(pid, Konashi.consts.OUTPUT);
        }

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE_LED_MODE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE_LED_MODE);
            this._pwmPeriod(pid, Konashi.consts.KONASHI_PWM_LED_PERIOD);

            isModeChanged = true;
        }

        if (!isModeChanged && ratio == this._pioPins[pid].pwmRatio) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmLedDrive(pid, ratio);
            that._pioPins[pid].pwmRatio = ratio;
        }, BLE_DELAY.NORMAL);
    }

    /**
     * @param {*} pid PIO pin number (0-7)
     * @param {*} period PWM period in microseconds
     */
    _pwmPeriod (pid, period) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmPeriod(pid, period);
            that._pioPins[pid].pwmPeriod = period;
        }, BLE_DELAY.NORMAL);
    }

    /**
     * @param {*} pid PIO pin number (0-7)
     * @param {*} duty PWM duty in microseconds
     */
    _pwmDuty (pid, duty) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE);
        } else if (duty == this._pioPins[pid].pwmDuty) {
            return;
        }

        const that = this;
        this._queue.add(() => {
            that._konashi.pwmDuty(pid, duty);
            that._pioPins[pid].pwmDuty = duty;
        }, BLE_DELAY.NORMAL);
    }

    /**
     *
     * @param {*} pid PIO pin number (0-7)
     * @param {*} duty PWM duty in microseconds
     */
    pwmServoDrive (pid, duty) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;

        let isModeChanged = false;

        if (this._pioPins[pid].pioMode == Konashi.consts.INPUT) {
            this._setMode(pid, Konashi.consts.OUTPUT);
        }

        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_ENABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_ENABLE);
            this._pwmPeriod(pid, Konashi.consts.KONASHI_PWM_SERVO_PERIOD);

            isModeChanged = true;
        }

        if (!isModeChanged && duty == this._pioPins[pid].pwmDuty) {
            return;
        }

        this._pwmDuty(pid, duty);
    }

    /**
     * @param {*} aio Konashi.consts.AIO0|AIO1|AIO2
     * @returns {number} stored analog input value, or -1 on error
     */
    getAnalogValue (aio) {
        if (!this._konashi) return -1; // ERROR
        if (aio < 0 || aio > 2) return -1; // ERROR

        return this._analogPins[aio].input;
    }

    /**
     * @param {*} aio Konashi.consts.AIO0|AIO1|AIO2
     * @returns {number} error code (-1: not connected, -2: out of range)
     */
    analogRead (aio) {
        if (!this._konashi) return -1; // ERROR
        if (aio < Konashi.consts.AIO0 || Konashi.consts.AIO2 < aio) return -2; // ERROR

        const that = this;
        this._konashi.analogRead(aio).then(v => {
            const value = v / Konashi.consts.KONASHI_ANALOG_REFERENCE * 1000;
            that._analogPins[aio].input = value;
        });
    }

    /**
     * @param {*} pid PIO pin number (0-7)
     * @returns {number} stored digital input value (Konashi.consts.HIGH|LOW)
     */
    getDigitalValue (pid) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;

        return this._pioPins[pid].input;
    }

    /**
     * @param {*} pid PIO pin number (0-7)
     */
    digitalRead (pid) {
        if (!this._konashi) return;
        if (pid < 0 || pid > 7) return;
        if (this._pioPins[pid].pwmMode != Konashi.consts.KONASHI_PWM_DISABLE) {
            this._setPwmMode(pid, Konashi.consts.KONASHI_PWM_DISABLE);
        }

        if (this._pioPins[pid].pinMode == Konashi.consts.OUTPUT) {
            this._setMode(pid, Konashi.consts.INPUT);
        }

        const that = this;
        this._konashi.digitalRead(pid).then(v => {
            that._pioPins[pid].input = v; // Konashi.consts.HIGH || Konashi.consts.LOW
        });
    }

    _startDigitalRead () {
        const that = this;
        this._onNotified = function (data) { // data is Number (0bXXXXXXXX)
            that._pioPins.forEach(pin => {
                if (pin.pioMode == Konashi.consts.INPUT) {
                    const input = (data >> pin.number) & 0x01; // 0: LOW, 1: HIGH
                    if (pin.input != input) {
                        pin.input = input;
                        that._onReceived(pin.number, input);
                        // argument should be (pin number 0 to 7) and (Konashi.consts.LOW|HIGH)
                    }
                }
            });
        };

        if (!this._konashi) return;
        this._konashi.startDigitalInputNotification(this._onNotified);
    }


    // { miscs and TBD functions
    /**
     * Log wrapper
     *
     * @param {string} message message to log
     */
    _log (message) {
        log.log(`kokoro pro: ${message}`);
    }
    // miscs and TBD functions}
}

module.exports = KokoroPro;
